import { useMemo, useState, type ReactNode } from "react";
import { format } from "date-fns";
import { ChevronDown, Loader2, Rocket, Upload } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modern/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { Separator } from "@/modern/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Site } from "@/bryntum-lib/models/Site";
import { cn } from "@/modern/utils";
import { SitePublishDomainBlock } from "./SitePublishDomainBlock";
import { SitePublishUrlRow } from "./SitePublishUrlRow";

interface SitePublishPanelProps {
  site: Site;
  boardSlug: string | undefined;
  boardId: number | undefined;
  isReadOnly: boolean;
  previewUrl: string;
  isLive: boolean;
  publishedAt: Date | null;
  includedCount: number;
  isBusy: boolean;
  isPublishing: boolean;
  isRepublishing: boolean;
  isDeploying: boolean;
  isPublishStarting: boolean;
  isDeployStarting: boolean;
  publishEnabled: boolean;
  publishTooltip: string | null;
  publishLiveEnabled: boolean;
  publishLiveTooltip: string | null;
  hasUnpublishedChanges: boolean;
  /** Unsaved or in-flight auto-save — drives immediate “pending” styling before server flags update. */
  hasUnsavedEdits?: boolean;
  onPreview: () => boolean | Promise<boolean>;
  onPublishLive: () => boolean | Promise<boolean>;
  onDomainChanged?: () => void;
}

function PanelSection({
  label,
  action,
  children,
  className,
}: {
  label: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-2 px-4 py-3", className)}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-medium text-muted-foreground">{label}</h4>
        {action}
      </div>
      {children}
    </section>
  );
}

type PublishConfirmAction = "preview" | "live";

type PublishTriggerTone = "idle" | "pending" | "ready";

function resolvePublishTriggerTone(
  publishEnabled: boolean,
  hasUnpublishedChanges: boolean,
  hasUnsavedEdits: boolean,
): PublishTriggerTone {
  if (publishEnabled) return "ready";
  if (hasUnpublishedChanges || hasUnsavedEdits) return "pending";
  return "idle";
}

function SitePublishConfirmDialog({
  action,
  isLive,
  open,
  onOpenChange,
  onConfirm,
  loading,
  blockReason,
  canConfirm,
}: {
  action: PublishConfirmAction | null;
  isLive: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
  blockReason: string | null;
  canConfirm: boolean;
}) {
  const copy = useMemo(() => {
    if (action === "live") {
      return {
        title: "Publish live?",
        description: (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              Swivu will update your preview site and deploy it to your custom domain so visitors see your latest pages
              and site settings.
            </p>
            <p>This may take a minute. You can keep working while publishing runs.</p>
          </div>
        ),
        confirmLabel: "Publish live",
        icon: Rocket,
      };
    }

    if (isLive) {
      return {
        title: "Update preview?",
        description: (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              Swivu will rebuild your preview site with the latest included pages and site settings, then update your
              shareable preview link.
            </p>
            <p>Your preview URL stays the same unless you changed the slug.</p>
          </div>
        ),
        confirmLabel: "Update preview",
        icon: Upload,
      };
    }

    return {
      title: "Publish preview?",
      description: (
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            Swivu will save your site settings and build a preview from your included pages at a shareable Swivu URL.
          </p>
          <p>You can iterate on content and publish the preview again before publishing to a custom domain.</p>
        </div>
      ),
      confirmLabel: "Publish preview",
      icon: Upload,
    };
  }, [action, isLive]);

  const ConfirmIcon = copy.icon;
  const confirmDisabled = loading || !action || !canConfirm;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!loading) onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {copy.description}
              {blockReason && (
                <p className="rounded-md bg-muted/60 px-2.5 py-2 text-xs leading-relaxed text-foreground">{blockReason}</p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          {confirmDisabled && blockReason ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button type="button" className="gap-1.5" disabled>
                    <ConfirmIcon className="size-4" />
                    {copy.confirmLabel}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[280px] border text-left">
                {blockReason}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button type="button" className="gap-1.5" disabled={confirmDisabled} onClick={() => void onConfirm()}>
              <ConfirmIcon className="size-4" />
              {copy.confirmLabel}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function SitePublishPanel({
  site,
  boardSlug,
  boardId,
  isReadOnly,
  previewUrl,
  isLive,
  publishedAt,
  includedCount,
  isBusy,
  isPublishing,
  isRepublishing,
  isDeploying,
  isPublishStarting,
  isDeployStarting,
  publishEnabled,
  publishTooltip,
  publishLiveEnabled,
  publishLiveTooltip,
  hasUnpublishedChanges,
  hasUnsavedEdits = false,
  onPreview,
  onPublishLive,
  onDomainChanged,
}: SitePublishPanelProps) {
  const [open, setOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<PublishConfirmAction | null>(null);

  const confirmOpen = confirmAction !== null;
  const confirmLoading =
    confirmAction === "preview"
      ? isPublishStarting || isPublishing || isRepublishing
      : confirmAction === "live"
        ? isDeployStarting || isDeploying
        : false;

  const handleConfirmOpenChange = (next: boolean) => {
    if (!next) setConfirmAction(null);
  };

  const handleConfirm = async () => {
    if (!confirmAction || confirmLoading) return;

    const canProceed = confirmAction === "preview" ? publishEnabled : publishLiveEnabled;
    if (!canProceed) return;

    const ok = confirmAction === "preview" ? await onPreview() : await onPublishLive();
    if (ok) {
      setConfirmAction(null);
      setOpen(false);
    }
  };

  const requestPreview = () => {
    if (previewBusy) return;
    setConfirmAction("preview");
  };

  const requestPublishLive = () => {
    if (deployBusy) return;
    setConfirmAction("live");
  };

  const publishedLabel =
    publishedAt && !Number.isNaN(publishedAt.getTime())
      ? format(publishedAt, "MMM d · h:mm a")
      : null;

  const previewLabel = useMemo(() => {
    if (isPublishStarting || isPublishing) return "Publishing preview…";
    if (isRepublishing) return "Updating preview…";
    if (isLive) return "Update preview";
    return "Publish preview";
  }, [isPublishStarting, isPublishing, isRepublishing, isLive]);

  const previewBusy = isPublishStarting || isPublishing || isRepublishing || isDeployStarting || isDeploying;
  const deployBusy = isDeploying || isDeployStarting || isPublishing || isRepublishing || isPublishStarting;

  const confirmBlockReason =
    confirmAction === "live"
      ? publishLiveEnabled
        ? null
        : publishLiveTooltip
      : publishEnabled
        ? null
        : publishTooltip;
  const confirmCanProceed = confirmAction === "live" ? publishLiveEnabled : publishEnabled;

  const showPublishLive = site.domainStatus === "active";

  const triggerTone = resolvePublishTriggerTone(publishEnabled, hasUnpublishedChanges, hasUnsavedEdits);
  const showPendingPublishStatus = triggerTone !== "idle";
  const showChangeDot = triggerTone !== "idle";
  const triggerTooltip =
    triggerTone === "ready"
      ? null
      : hasUnsavedEdits && !publishEnabled
        ? "Saving your edits…"
        : publishTooltip;

  const triggerButton = (
    <Button
      type="button"
      size="sm"
      variant={triggerTone === "ready" ? "default" : "outline"}
      className={cn(
        "relative gap-1.5 transition-[box-shadow,background-color,border-color,color]",
        triggerTone === "idle" && "text-muted-foreground",
        triggerTone === "pending" && "border-amber-500/40 bg-background",
        triggerTone === "ready" && "shadow-sm ring-1 ring-primary/25",
      )}
      aria-label={
        triggerTone === "ready"
          ? "Publish — preview ready"
          : triggerTone === "pending"
            ? "Publish — changes pending"
            : "Publish site"
      }
    >
      {showChangeDot && (
        <span
          className={cn(
            "absolute -right-0.5 -top-0.5 size-2 rounded-full ring-2 ring-background",
            triggerTone === "ready" ? "bg-amber-500" : "bg-amber-500/80",
            hasUnsavedEdits && !publishEnabled && "animate-pulse",
          )}
          aria-hidden
        />
      )}
      {isBusy && <Loader2 className="size-4 animate-spin" />}
      {!isBusy && (
        <Upload className={cn("size-4", triggerTone === "idle" && "opacity-70")} />
      )}
      Publish
      <ChevronDown className="size-3.5 opacity-70" />
    </Button>
  );

  const popoverTrigger = triggerTooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="max-w-[280px] border text-left">
        {triggerTooltip}
      </TooltipContent>
    </Tooltip>
  ) : (
    <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
  );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
      {popoverTrigger}
      <PopoverContent
        align="end"
        sideOffset={8}
        className="flex max-h-[min(85vh,560px)] w-[min(100vw-2rem,400px)] flex-col overflow-hidden p-0"
      >
        <div className="shrink-0">
          <div className="flex items-start justify-between gap-3 px-4 pb-3 pt-4">
            <div className="min-w-0 space-y-0.5">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <p className="text-sm font-semibold text-foreground">Publish site</p>
                {showPendingPublishStatus && (
                  <span className="text-[11px] text-amber-500" role="status" aria-live="polite">
                    Unpublished changes
                  </span>
                )}
              </div>
              {publishedLabel && isLive && (
                <p className="text-[11px] text-muted-foreground">Last preview · {publishedLabel}</p>
              )}
            </div>
            <div className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              {includedCount} {includedCount === 1 ? "page" : "pages"}
            </div>
          </div>

          <Separator />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <PanelSection label="Preview URL">
            <SitePublishUrlRow
              url={previewUrl}
              emptyLabel="Publish a preview to get a shareable link"
            />
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Share this Swivu preview link while you iterate. Updates when you publish a preview.
            </p>
          </PanelSection>

          <Separator />

          <PanelSection
            label="Custom domain"
            action={
              site.domainStatus === "unset" && !isReadOnly ? (
                <span className="text-[11px] text-muted-foreground">Optional</span>
              ) : null
            }
          >
            <SitePublishDomainBlock
              site={site}
              boardSlug={boardSlug}
              boardId={boardId}
              isReadOnly={isReadOnly}
              isBusy={isBusy}
              onDomainChanged={onDomainChanged}
            />
          </PanelSection>
        </div>

        {(!isReadOnly || Boolean(publishTooltip)) && (
          <div className="shrink-0 border-t border-border">
            <div className="space-y-2 px-4 py-3">
              <Button
                type="button"
                variant={publishEnabled && !isReadOnly ? "default" : "outline"}
                className={cn("w-full gap-1.5", (!publishEnabled || isReadOnly) && "text-muted-foreground")}
                disabled={previewBusy || isReadOnly}
                title={isReadOnly ? (publishTooltip ?? undefined) : undefined}
                onClick={requestPreview}
              >
                <Upload className="size-4" />
                {previewLabel}
              </Button>

              {showPublishLive && (
                <Button
                  type="button"
                  variant={publishLiveEnabled && !isReadOnly ? "default" : "outline"}
                  className={cn("w-full gap-1.5", (!publishLiveEnabled || isReadOnly) && "text-muted-foreground")}
                  disabled={deployBusy || isReadOnly}
                  title={isReadOnly ? (publishLiveTooltip ?? undefined) : undefined}
                  onClick={requestPublishLive}
                >
                  <Rocket className="size-4" />
                  Publish live
                </Button>
              )}
              {isReadOnly && publishTooltip ? (
                <p className="text-[11px] leading-snug text-muted-foreground">{publishTooltip}</p>
              ) : null}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>

      <SitePublishConfirmDialog
        action={confirmAction}
        isLive={isLive}
        open={confirmOpen}
        onOpenChange={handleConfirmOpenChange}
        onConfirm={handleConfirm}
        loading={confirmLoading}
        blockReason={confirmCanProceed ? null : confirmBlockReason}
        canConfirm={confirmCanProceed}
      />
    </>
  );
}
